




































































































import { Component, Watch, Vue } from 'vue-property-decorator';
import KpiDashboardCard from '@/components/KpiTargets/KpiDashboardCard.vue';
import KpiDashboardItemDto from '@/models/KpiTargets/KpiDashboardItemDto';
import { KpiTargetsModule } from '@/store/modules/kpiTargets';
import { AgencySettingsModule } from '@/store/modules/agencySettings';
import { getFiscalYearOptions } from '@/lib/fiscalYears';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

@Component({
  name: 'KpiDashboard',
  components: { KpiDashboardCard }
})
export default class KpiDashboard extends Vue {

  periodType = 1;
  referenceDate: string = '';
  rpFiscalYear: number | null = null;
  selectedReportingPeriodId: string | null = null;

  get loading(): boolean {
    return KpiTargetsModule.loading;
  }

  get items(): KpiDashboardItemDto[] {
    return KpiTargetsModule.kpiDashboardItems;
  }

  get hasNoItems(): boolean {
    return this.items.length === 0;
  }

  // Each target is categorised by its most-specific scope. A desk-level
  // target has a branchId set as well (desks belong to branches), and a
  // user-level target may have branchId/deskId set, so the filters below
  // exclude more specific scopes to avoid double-listing.
  get agencyItems(): KpiDashboardItemDto[] {
    return this.items.filter(i => {
      const t = i.target;
      return !t.branchId && !t.deskId && !t.agencyUserId;
    });
  }

  get branchItems(): KpiDashboardItemDto[] {
    return this.items.filter(i => {
      const t = i.target;
      return !!t.branchId && !t.deskId && !t.agencyUserId;
    });
  }

  get deskItems(): KpiDashboardItemDto[] {
    return this.items.filter(i => {
      const t = i.target;
      return !!t.deskId && !t.agencyUserId;
    });
  }

  get userItems(): KpiDashboardItemDto[] {
    return this.items.filter(i => !!i.target.agencyUserId);
  }

  get todayISO(): string {
    const d = new Date();
    return this.toISO(d);
  }

  get periodTypeLabel(): string {
    switch (this.periodType) {
      case 1: return 'daily';
      case 2: return 'weekly';
      case 3: return 'monthly';
      case 4: return 'quarterly';
      case 5: return 'custom';
      default: return '';
    }
  }

  get reportingPeriods() {
    return AgencySettingsModule.reportingPeriods;
  }

  get hasReportingPeriods(): boolean {
    return this.reportingPeriods.length > 0;
  }

  get rpFiscalYearItems(): number[] {
    return getFiscalYearOptions();
  }

  get filteredReportingPeriods() {
    const list = this.rpFiscalYear
      ? this.reportingPeriods.filter((p: any) => p.fiscalYear === this.rpFiscalYear)
      : this.reportingPeriods;
    // Sort by fiscal year desc then period number asc, and attach displayText for v-select
    return [...list]
      .sort((a: any, b: any) => {
        if (a.fiscalYear !== b.fiscalYear) return b.fiscalYear - a.fiscalYear;
        return a.periodNumber - b.periodNumber;
      })
      .map((p: any) => ({
        id: p.id,
        displayText: p.label ? p.label : 'P' + p.periodNumber + ' FY' + p.fiscalYear,
      }));
  }

  get selectedReportingPeriod(): any {
    if (!this.selectedReportingPeriodId) return null;
    return this.reportingPeriods.find((p: any) => p.id === this.selectedReportingPeriodId) || null;
  }

  get selectedPeriodDateRange(): string {
    const rp = this.selectedReportingPeriod;
    if (!rp) return '';
    const start = rp.startDate ? rp.startDate.substring(0, 10) : '';
    const end = rp.endDate ? rp.endDate.substring(0, 10) : '';
    return start + ' — ' + end;
  }

  get displayPeriod(): string {
    if (!this.referenceDate) return '';
    const d = new Date(this.referenceDate + 'T00:00:00');
    switch (this.periodType) {
      case 1: {
        // Daily: "Mon 29 Mar 2026"
        return DAYS[d.getDay()] + ' ' + d.getDate() + ' ' + MONTHS_SHORT[d.getMonth()] + ' ' + d.getFullYear();
      }
      case 2: {
        // Weekly: show Mon-Sun of the week containing referenceDate
        const mon = this.getMonday(d);
        const sun = new Date(mon);
        sun.setDate(mon.getDate() + 6);
        const monStr = mon.getDate() + ' ' + MONTHS_SHORT[mon.getMonth()];
        const sunStr = sun.getDate() + ' ' + MONTHS_SHORT[sun.getMonth()] + ' ' + sun.getFullYear();
        return monStr + ' — ' + sunStr;
      }
      case 3: {
        // Monthly: "March 2026"
        return MONTHS[d.getMonth()] + ' ' + d.getFullYear();
      }
      case 4: {
        // Quarterly: "Q1 2026"
        const q = Math.floor(d.getMonth() / 3) + 1;
        return 'Q' + q + ' ' + d.getFullYear();
      }
      default:
        return '';
    }
  }

  get isCurrent(): boolean {
    const today = new Date();
    const ref = new Date(this.referenceDate + 'T00:00:00');
    switch (this.periodType) {
      case 1:
        return this.referenceDate === this.todayISO;
      case 2:
        return this.getMonday(ref).getTime() === this.getMonday(today).getTime();
      case 3:
        return ref.getMonth() === today.getMonth() && ref.getFullYear() === today.getFullYear();
      case 4:
        return Math.floor(ref.getMonth() / 3) === Math.floor(today.getMonth() / 3) && ref.getFullYear() === today.getFullYear();
      default:
        return false;
    }
  }

  itemKey(item: KpiDashboardItemDto): string {
    return item.target ? item.target.id : '';
  }

  navigatePeriod(direction: number) {
    const d = new Date(this.referenceDate + 'T00:00:00');
    switch (this.periodType) {
      case 1:
        d.setDate(d.getDate() + direction);
        break;
      case 2:
        d.setDate(d.getDate() + (7 * direction));
        break;
      case 3:
        d.setMonth(d.getMonth() + direction, 1);
        break;
      case 4:
        d.setMonth(d.getMonth() + (3 * direction), 1);
        break;
    }
    this.referenceDate = this.toISO(d);
    this.loadDashboard();
  }

  goToCurrent() {
    this.referenceDate = this.todayISO;
    this.loadDashboard();
  }

  @Watch('periodType')
  onPeriodTypeChanged() {
    if (this.periodType === 5) {
      // Pick current reporting period by default if none selected
      if (!this.selectedReportingPeriodId) {
        const current = this.findCurrentReportingPeriod();
        if (current) {
          this.selectedReportingPeriodId = current.id;
          this.rpFiscalYear = current.fiscalYear;
        }
      }
    }
    this.loadDashboard();
  }

  onRpFiscalYearChanged() {
    // Clear selected period if it no longer matches the filter
    if (this.rpFiscalYear && this.selectedReportingPeriod
        && this.selectedReportingPeriod.fiscalYear !== this.rpFiscalYear) {
      this.selectedReportingPeriodId = null;
    }
  }

  onReportingPeriodChanged() {
    this.loadDashboard();
  }

  async loadDashboard() {
    // For custom period, skip loading until a reporting period is chosen
    if (this.periodType === 5 && !this.selectedReportingPeriodId) {
      KpiTargetsModule.clearKpiDashboardItems();
      return;
    }
    await KpiTargetsModule.loadKpiDashboard({
      referenceDate: this.referenceDate,
      periodType: this.periodType,
      reportingPeriodId: this.periodType === 5 ? this.selectedReportingPeriodId : null,
    });
  }

  findCurrentReportingPeriod(): any {
    const today = this.todayISO;
    return this.reportingPeriods.find((p: any) => {
      const start = p.startDate ? p.startDate.substring(0, 10) : '';
      const end = p.endDate ? p.endDate.substring(0, 10) : '';
      return start && end && start <= today && today <= end;
    }) || null;
  }

  async mounted() {
    this.referenceDate = this.todayISO;
    // Ensure reporting periods are loaded so we can populate the dropdown
    // and default to the current one if any exist.
    if (AgencySettingsModule.reportingPeriods.length === 0) {
      await AgencySettingsModule.getReportingPeriods();
    }
    const current = this.findCurrentReportingPeriod();
    if (current) {
      this.selectedReportingPeriodId = current.id;
      this.rpFiscalYear = current.fiscalYear;
      this.periodType = 5;
      // periodType watcher will trigger loadDashboard
      return;
    }
    await this.loadDashboard();
  }

  private toISO(d: Date): string {
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }

  private getMonday(d: Date): Date {
    const result = new Date(d);
    const day = result.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    result.setDate(result.getDate() + diff);
    return result;
  }
}
